import { useEffect } from 'react';
import { useKeyboardShortcut, useKeyboardShortcuts } from '@gonfalon/keyboard-shortcuts/react';
import { useLatestRef } from '@gonfalon/react/useLatestRef';

import { useCommandRegistry } from '../components/command-registry-provider';
import { getShortcutCategory } from '../utils/get-shortcut-category';

import { useCommandPaletteState } from './use-command-palette-state';

export interface UseCommandPaletteKeyboardManagementOptions {
  shortcut?: string;
  executeCommandById: (
    commandId: string | number,
    options: {
      executionType: 'action-execution' | 'keyboard-shortcut';
      message: string;
    },
  ) => Promise<void>;
  openPalette: (trigger: 'button' | 'keyboard') => void;
  closePalette: () => void;
}

/**
 * Bind the global shortcut (default `meta+k` / `ctrl+k`) and any
 * per-command `shortcut` declarations.
 *
 * The previous incarnation walked the dual (service / component)
 * registry and built a signature string to avoid spurious re-registers
 * when filtering shrunk the visible list. With the flat registry, the
 * `commands` reference only changes on real register/unregister, so a
 * plain `[commands, isOpen]` dep on the effect is correct -- no
 * signature dance.
 *
 * `executeCommandById` is held in a ref so a fresh closure identity
 * from the parent doesn't re-register every shortcut.
 */
export function useCommandPaletteKeyboardManagement({
  shortcut = 'meta+k',
  executeCommandById,
  openPalette,
  closePalette,
}: UseCommandPaletteKeyboardManagementOptions): void {
  const { isOpen } = useCommandPaletteState();
  const { commands } = useCommandRegistry();
  const shortcuts = useKeyboardShortcuts();

  // Main palette-open shortcut.
  useKeyboardShortcut({
    shortcut,
    scope: 'command-palette',
    label: 'Open command palette',
    category: 'General',
    handler: () => {
      if (isOpen) {
        closePalette();
      } else {
        openPalette('keyboard');
      }
    },
  });

  const executeCommandByIdRef = useLatestRef(executeCommandById);

  useEffect(() => {
    // Snapshot id list for cleanup. Iterating Object.values(commands)
    // is O(N) in the registry; fine for the small N we deal with.
    const registeredIds: string[] = [];
    for (const command of Object.values(commands)) {
      if (!command.shortcut) {
        continue;
      }
      const id = `cmd-${command.id}`;
      registeredIds.push(id);
      shortcuts.register(
        [
          {
            id,
            hotkey: command.shortcut,
            label: command.label,
            // Bucket label in the keyboard-shortcuts dialog. Mirrors
            // the activation rule: a scope (action without `run`) is
            // a "Container"; everything else is a "Command." See
            // `getShortcutCategory` for the full table.
            category: getShortcutCategory(command),
            // Shortcuts are disabled while the palette is open: pressing
            // their hotkey then would compete with the palette's own
            // input handling. They re-arm on close.
            enabled: !isOpen,
            handler: async () => {
              await executeCommandByIdRef.current(command.id, {
                executionType: 'keyboard-shortcut',
                message: `Failed to execute keyboard shortcut for ${command.type} command: ${command.label}`,
              });
            },
          },
        ],
        'command-palette',
      );
    }
    return () => {
      for (const id of registeredIds) {
        shortcuts.unregisterById(id);
      }
    };
  }, [commands, isOpen, shortcuts]);
}
